import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import MapView from '../components/MapView';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import { Calendar, MapPin, Trophy, Users, Tent, House, Heart, ShareNetwork, Star, Phone, EnvelopeSimple, Globe } from '@phosphor-icons/react';
import { Reviews, RideShare, PhotoGallery, NearbyEvents } from '../components/EventExtras';
import { toast } from 'sonner';
import Seo from '../components/Seo';

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [lodging, setLodging] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/events/${id}`);
        if (cancelled) return;
        setEvent(r.data);
        if (r.data?.lat && r.data?.lng) {
          try {
            const h = await api.get(`/hotels/search`, {
              params: { lat: r.data.lat, lng: r.data.lng, city: r.data.city || '' },
            });
            if (!cancelled) setLodging(h.data?.results || []);
          } catch {
            try {
              const r2 = await api.get(`/lodging/near/${id}`);
              if (!cancelled) {
                setLodging(r2.data.map(l => ({
                  id: l.lodging_id, name: l.name, rating: l.rating, price: l.price_per_night,
                  image: l.image, distance_miles: l.distance_miles, url: l.booking_url,
                  source: 'curated', currency: 'USD',
                })));
              }
            } catch { /* ignore */ }
          }
        }
      } catch {
        if (!cancelled) setEvent(false);
      }
      if (user) {
        try {
          const s = await api.get('/me/saved-events');
          if (!cancelled) setSaved(s.data.some(e => e.event_id === id));
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [id, user]);

  const toggleSave = async () => {
    if (!user) { toast.info('Sign in to save events'); return; }
    if (saved) {
      await api.delete(`/me/saved-events/${id}`);
      setSaved(false);
      toast.success('Removed from saved');
    } else {
      await api.post(`/me/saved-events/${id}`);
      setSaved(true);
      toast.success('Saved to your dashboard');
    }
  };

  const share = async () => {
    // Use the OG snapshot URL so social platforms (iMessage/Facebook/Twitter/etc)
    // that don't execute JS still see rich meta tags & preview cards.
    // Humans are auto-redirected to the SPA event page.
    const backend = process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${backend}/api/og/events/${event.event_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: event.name, url: shareUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied — ready to share');
    }
  };

  if (event === null) return <div className="max-w-7xl mx-auto p-12"><div className="h-64 bg-muted animate-pulse rounded-2xl" /></div>;
  if (event === false) return (
    <div className="max-w-7xl mx-auto p-12 text-center">
      <Seo path={`/events/${id}`} title="Event not found" description="This pow wow could not be found." noindex />
      <h1 className="font-display text-3xl">Event not found</h1>
    </div>
  );

  // Build rich SEO description from event data
  const dateStr = event.start_date === event.end_date
    ? event.start_date
    : `${event.start_date} – ${event.end_date}`;
  const seoTitle = `${event.name} · ${event.city}, ${event.state} · ${event.start_date}`;
  const seoDesc = (event.description || '').slice(0, 220).trim() ||
    `${event.name} — a ${event.indoor ? 'indoor' : 'outdoor'} pow wow hosted by ${event.tribe} in ${event.city}, ${event.state} on ${dateStr}.${event.total_prize_pool > 0 ? ` $${event.total_prize_pool.toLocaleString()} in prize payouts.` : ''} Grand entry, dance categories, vendors, and nearby lodging.`;
  const seoImage = event.hero_image || 'https://powwowfinder.com/logo.png';
  const eventJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    description: event.description,
    startDate: event.start_date,
    endDate: event.end_date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: event.venue,
      address: {
        '@type': 'PostalAddress',
        streetAddress: event.address,
        addressLocality: event.city,
        addressRegion: event.state,
        addressCountry: event.country,
      },
      geo: { '@type': 'GeoCoordinates', latitude: event.lat, longitude: event.lng },
    },
    organizer: { '@type': 'Organization', name: event.tribe },
    image: event.hero_image ? [event.hero_image] : undefined,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: `https://powwowfinder.com/events/${event.event_id}`,
      validFrom: event.start_date,
    },
    url: `https://powwowfinder.com/events/${event.event_id}`,
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://powwowfinder.com/' },
      { '@type': 'ListItem', position: 2, name: 'Pow Wows', item: 'https://powwowfinder.com/events' },
      { '@type': 'ListItem', position: 3, name: event.name, item: `https://powwowfinder.com/events/${event.event_id}` },
    ],
  };

  return (
    <div>
      <Seo
        path={`/events/${event.event_id}`}
        title={seoTitle}
        description={seoDesc}
        image={seoImage}
        type="event"
        keywords={`${event.name}, ${event.city} powwow, ${event.state} powwow, ${event.tribe}, pow wow ${event.start_date}, ${(event.dance_categories || []).join(', ')}`}
        jsonLd={[eventJsonLd, breadcrumbJsonLd]}
      />
      {/* HERO */}
      <div className="relative h-[55vh] min-h-[420px] overflow-hidden">
        {event.hero_image ? (
          <img
            src={event.hero_image}
            alt={`${event.name} — ${event.tribe} pow wow in ${event.city}, ${event.state} on ${event.start_date}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-forest pattern-weave" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 text-bone">
          <p className="label text-sand mb-3">{event.tribe}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl text-shadow-hero">{event.name}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm">
            <span className="flex items-center gap-1.5"><Calendar size={16} weight="duotone" /> {event.start_date} – {event.end_date}</span>
            <span className="flex items-center gap-1.5"><MapPin size={16} weight="duotone" /> {event.city}, {event.state}</span>
            <span className="flex items-center gap-1.5">{event.indoor ? <House size={16} weight="duotone" /> : <Tent size={16} weight="duotone" />} {event.indoor ? 'Indoor' : 'Outdoor'}</span>
            {event.total_prize_pool > 0 && <span className="flex items-center gap-1.5"><Trophy size={16} weight="duotone" /> ${event.total_prize_pool.toLocaleString()} pool</span>}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={toggleSave} className={saved ? 'bg-sand text-ink hover:bg-sand/90 rounded-full' : 'bg-terracotta hover:bg-terracotta/90 text-white rounded-full'} data-testid="event-save-btn">
              <Heart size={16} weight={saved ? 'fill' : 'regular'} className="mr-2" /> {saved ? 'Saved' : 'Save event'}
            </Button>
            <Button onClick={share} variant="outline" className="rounded-full border-bone/40 text-bone hover:bg-bone/10 hover:text-bone bg-transparent" data-testid="event-share-btn">
              <ShareNetwork size={16} className="mr-2" /> Share
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[1fr_360px] gap-10">
        <main className="space-y-12">
          <section>
            <h2 className="font-display text-2xl mb-4">About this gathering</h2>
            <p className="text-muted-foreground leading-relaxed text-base">{event.description}</p>
          </section>

          {event.grand_entry_times?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl mb-4">Grand Entry</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {event.grand_entry_times.map((t, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-4 text-center">
                    <p className="font-display text-lg text-turquoise">{t}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {event.schedule?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl mb-4">Schedule</h2>
              <div className="bg-card border border-border rounded-2xl divide-y divide-border">
                {event.schedule.map((s, i) => (
                  <div key={i} className="p-4 flex items-center gap-4">
                    <span className="label w-20 text-terracotta">{s.day}</span>
                    <span className="font-display text-lg w-28">{s.time}</span>
                    <span className="text-sm">{s.activity}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {event.payouts?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Trophy size={22} weight="duotone" className="text-sand" /> Prize Payouts</h2>
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr><th className="text-left p-4 label">Category</th><th className="text-left p-4 label">Place</th><th className="text-right p-4 label">Amount</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {event.payouts.map((p, i) => (
                      <tr key={i} data-testid={`payout-row-${i}`}>
                        <td className="p-4 font-medium">{p.category}</td>
                        <td className="p-4 text-muted-foreground">{p.place}</td>
                        <td className="p-4 text-right font-display text-lg text-gold">${p.amount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {event.head_staff?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Users size={22} weight="duotone" /> Head Staff</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {event.head_staff.map((s, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-4">
                    <p className="label text-terracotta mb-1">{s.role}</p>
                    <p className="font-display text-lg">{s.name}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {event.dance_categories?.length > 0 && (
            <section>
              <h2 className="font-display text-2xl mb-4">Dance categories</h2>
              <div className="flex flex-wrap gap-2">
                {event.dance_categories.map(c => (
                  <span key={c} className="bg-muted text-foreground rounded-full px-4 py-1.5 text-sm border border-border">{c}</span>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="font-display text-2xl mb-4">Location</h2>
            <p className="text-muted-foreground mb-4">{event.venue} · {event.address}</p>
            <div className="rounded-2xl overflow-hidden border border-border">
              <MapView events={[event]} center={[event.lat, event.lng]} zoom={11} height="400px" />
            </div>
          </section>

          <RideShare eventId={event.event_id} />
          <PhotoGallery eventId={event.event_id} />
          <Reviews eventId={event.event_id} />
          <NearbyEvents eventId={event.event_id} />
        </main>

        <aside className="space-y-6">
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-display text-xl mb-4">Event details</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Admission</dt><dd className="font-medium">{event.admission}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Camping</dt><dd className="font-medium">{event.camping ? 'Yes' : 'No'}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">RV hookups</dt><dd className="font-medium">{event.rv_hookups ? 'Yes' : 'No'}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Family friendly</dt><dd className="font-medium">{event.family_friendly ? 'Yes' : 'No'}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Alcohol-free</dt><dd className="font-medium">{event.alcohol_free ? 'Yes' : 'No'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Accessibility</dt><dd className="font-medium text-right">{event.accessibility}</dd></div>
            </dl>
          </div>

          {(event.contact_email || event.contact_phone || event.website) && (
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-display text-xl mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                {event.contact_email && <li className="flex items-center gap-2"><EnvelopeSimple size={16} weight="duotone" /> <a href={`mailto:${event.contact_email}`} className="hover:text-terracotta">{event.contact_email}</a></li>}
                {event.contact_phone && <li className="flex items-center gap-2"><Phone size={16} weight="duotone" /> {event.contact_phone}</li>}
                {event.website && <li className="flex items-center gap-2"><Globe size={16} weight="duotone" /> <a href={event.website} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">Website</a></li>}
              </ul>
            </div>
          )}

          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-display text-xl mb-4 flex items-center justify-between">
              <span>Nearby hotels</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans">via Booking.com</span>
            </h3>
            {lodging.length === 0 && <p className="text-sm text-muted-foreground">No hotels found nearby.</p>}
            <ul className="space-y-3">
              {lodging.slice(0, 6).map(l => (
                <li key={l.id} className="flex items-start gap-3" data-testid={`lodging-${l.id}`}>
                  {l.image ? (
                    <img src={l.image} alt={l.name} className="w-14 h-14 rounded-lg object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-muted grid place-items-center text-muted-foreground"><House size={20} weight="duotone" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="font-medium text-sm leading-tight truncate hover:text-turquoise block">
                      {l.name}
                    </a>
                    <p className="text-xs text-muted-foreground">
                      {l.distance_miles != null ? `${l.distance_miles} mi away` : ''}
                      {l.stars ? ` · ${l.stars}★` : ''}
                    </p>
                    <p className="text-xs mt-0.5">
                      {l.price != null && <><span className="text-gold font-semibold">${Math.round(l.price)}</span> <span className="text-muted-foreground">/night</span></>}
                      {l.rating != null && <> · <Star size={10} weight="fill" className="inline text-sand" /> {l.rating}{l.review_count ? <span className="text-muted-foreground"> ({l.review_count})</span> : null}</>}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
